import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent, FilterGroup } from '@custom-components/general/table-filter-bar';
import { partnerFilterGroup } from '@core/utils/partner-filter-group.util';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import {
  APP_PATHS,
  CONNECTION_STATE_LABEL_KEYS,
  CONNECTION_TYPES,
  DEVICE_CONNECTIVITY_STATUS,
  DEVICES_TABLE_COLUMNS_CONFIG,
  INSTALLATION_STATE_LABEL_KEYS,
  PAGINATION_DEFAULT,
  XEMEX_OID,
} from '@core/constants';
import { DevicePayloadModel, getUserActivePartner } from '@core/models';
import { resolveDeviceStates } from '@core/helpers/device-state.helper';
import { DeviceStore } from '@root/app/store/device.store';
import { UserStore } from '@root/app/store/user.store';
import { CompanyService } from '@core/services';
import {
  deviceTypeFilterGroup,
  installationStateFilterGroup,
} from '@core/utils/device-filter-groups.util';

@Component({
  selector: 'xemex-devices-page',
  imports: [
    TableComponent,
    TableFilterBarComponent,
    PageHeaderComponent,
    PageBodyComponent,
    TranslateModule,
  ],
  providers: [DeviceStore],
  templateUrl: './devices-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './devices-page.component.scss',
})
export class DevicesPageComponent {
  readonly deviceStore = inject(DeviceStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly companyService = inject(CompanyService);
  private readonly userStore = inject(UserStore);

  protected readonly APP_PATHS = APP_PATHS;
  protected readonly columns = DEVICES_TABLE_COLUMNS_CONFIG;

  /** The partner pill is only offered to Xemex users, who can see every partner's data. */
  protected readonly isXemexUser = computed(
    () => getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );

  private readonly requestedInstallationState = signal(
    this.route.snapshot.queryParamMap.get('installationState') ?? '',
  );

  /** A backend that ignores the installationState filter (old backend, D473) returns every
   * device: rows that carry a different state are dropped so the list never contradicts the
   * filter, and the count follows the rows that remain. */
  private readonly visibleDevices = computed(() => {
    const { devices } = this.deviceStore.deviceData();
    const wanted = this.requestedInstallationState();
    if (!wanted) return devices;
    const states = new Set(wanted.split(','));
    return devices.filter(d => !d.installationState || states.has(d.installationState));
  });

  protected readonly tableData = computed<Record<string, unknown>[]>(() =>
    this.visibleDevices().map(device => {
      const { installationState, connectionState } = resolveDeviceStates(device);
      return {
        ...device,
        installationStateLabel: installationState
          ? this.translate.instant(INSTALLATION_STATE_LABEL_KEYS[installationState])
          : '',
        connectionStateLabel: connectionState
          ? this.translate.instant(CONNECTION_STATE_LABEL_KEYS[connectionState])
          : '',
      };
    }),
  );

  protected readonly totalCount = computed(() => {
    const { devices, numberOfItems } = this.deviceStore.deviceData();
    return this.visibleDevices().length < devices.length
      ? this.visibleDevices().length
      : numberOfItems;
  });

  protected readonly filterGroups = computed<FilterGroup[]>(() => [
    deviceTypeFilterGroup(this.deviceStore, this.translate),
    {
      key: 'status',
      label: this.translate.instant('devices.filter.status.title'),
      options: DEVICE_CONNECTIVITY_STATUS.map(s => s.index),
    },
    installationStateFilterGroup(this.translate),
    {
      key: 'connectionType',
      label: this.translate.instant('devices.filter.connectionType.title'),
      options: CONNECTION_TYPES.map(c => c.index),
    },
    ...(this.isXemexUser()
      ? [
          partnerFilterGroup(
            this.companyService,
            this.translate.instant('devices.filter.partner'),
            this.translate.instant('general.placeholderInput'),
          ),
        ]
      : []),
  ]);

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Devices,
    row['deviceId'] as string,
  ];

  /** Deep-linked from the dashboard's devices widget, e.g. `?status=Online&installationState=Installed`
   * — pre-selects the "status" and "installationState" pills (the filter bar only reads this once,
   * on init). */
  protected readonly initialFilters = computed<Record<string, string[]>>(() => {
    const status = this.route.snapshot.queryParamMap.get('status');
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    const filters: Record<string, string[]> = {};
    const installationState = this.route.snapshot.queryParamMap.get('installationState');
    if (status) filters['status'] = [status];
    if (installationState) filters['installationState'] = [installationState];
    if (partnerOid && this.isXemexUser()) filters['partner'] = [partnerOid];
    return filters;
  });

  constructor() {
    this.getDevices();
    if (!this.deviceStore.deviceTypes()) {
      this.deviceStore.getDeviceTypesMethod();
    }
  }

  private getDevices(): void {
    const partnerOid = this.route.snapshot.queryParamMap.get('partnerOid');
    const locationId = this.route.snapshot.queryParamMap.get('locationId');
    const status = this.route.snapshot.queryParamMap.get('status');
    const installationState = this.route.snapshot.queryParamMap.get('installationState');
    if (locationId) {
      this.deviceStore.getDevicesFilterMethod({ locationId });
      return;
    }
    if (partnerOid) {
      this.deviceStore.getDevicesFilterMethod({ partnerOid });
      return;
    }
    if (status || installationState) {
      this.deviceStore.getDevicesFilterMethod({
        connectionState: status ?? '',
        installationState: installationState ?? '',
      });
      return;
    }
    this.deviceStore.getDevicesMethod(PAGINATION_DEFAULT.offset);
  }

  protected onSearchChange(query: string): void {
    this.deviceStore.getDevicesFilterMethod({ deviceLDN: query });
  }

  protected onFilterChange(filters: Record<string, string[]>): void {
    const partnerOid = filters['partner']?.[0];
    this.requestedInstallationState.set(filters['installationState']?.join(',') ?? '');
    const payload: DevicePayloadModel = {
      deviceType: filters['deviceType']?.join(',') ?? '',
      connectionState: filters['status']?.join(',') ?? '',
      installationState: filters['installationState']?.join(',') ?? '',
      connectionType: filters['connectionType']?.join(',') ?? '',
      partnerOid,
    };
    this.deviceStore.getDevicesFilterMethod(payload);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { partnerOid: partnerOid ?? null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  protected onPageChange(evt: PageEvent): void {
    this.deviceStore.getDevicesMethod(evt.pageIndex);
  }

  protected onRefreshClicked(): void {
    this.deviceStore.refreshDevicesMethod();
  }
}
